import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { sendPhoneOTP, verifyPhoneOTP, signOutUser, useAuth } from '@weebee/api';
import { BrandMark } from '@/components/ui/brand-mark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type LoginSearch = { error?: string };

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    error: typeof search['error'] === 'string' ? search['error'] : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const authState = useAuth();
  const navigate = useNavigate();
  const { error: searchError } = useSearch({ from: '/login' });
  const [phone, setPhone] = useState('+91');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Sign out non-admin users that were redirected here
  useEffect(() => {
    if (searchError === 'unauthorized' && authState.status === 'authenticated') {
      void signOutUser();
    }
  }, [searchError, authState.status]);

  useEffect(() => {
    if (authState.status === 'authenticated' && searchError !== 'unauthorized') {
      void navigate({ to: '/' });
    }
  }, [authState.status, navigate, searchError]);

  if (authState.status === 'authenticated' && searchError !== 'unauthorized') {
    return null;
  }

  async function handleSendOTP(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPhoneOTP(phone.trim());
      setOtpSent(true);
      setTimeout(() => otpInputRef.current?.focus(), 50);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOTP(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyPhoneOTP(phone.trim(), otp.trim());
      void navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    setOtpSent(false);
    setOtp('');
    setError('');
  }

  return (
    <div className="wb-scroll" style={{ height: '100dvh', background: 'var(--paper)' }}>
      <div className="mx-auto flex min-h-full w-full max-w-[420px] flex-col items-center justify-center px-6 py-12">
        <div className="flex w-full flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-1.5 text-center">
            <h1 className="wb-h1" style={{ fontSize: 32 }}>
              WeeBee Admin
            </h1>
            <BrandMark className="h-12 mb-1.5" alt="Go" />
            <p className="wb-sub" style={{ maxWidth: 300 }}>
              {otpSent ? `Enter the code sent to ${phone}` : 'Sign in with your phone number'}
            </p>
          </div>

          {searchError === 'unauthorized' && !error && (
            <div
              className="wb-small w-full rounded-md p-3"
              style={{
                background: 'color-mix(in oklab, var(--danger) 12%, var(--surface))',
                color: 'var(--danger)',
              }}
            >
              You don't have admin access. Contact an administrator.
            </div>
          )}

          {error && (
            <div
              className="wb-small w-full rounded-md p-3"
              style={{
                background: 'color-mix(in oklab, var(--danger) 12%, var(--surface))',
                color: 'var(--danger)',
              }}
            >
              {error}
            </div>
          )}

          {!otpSent ? (
            <form onSubmit={handleSendOTP} className="flex w-full flex-col gap-4">
              <div>
                <label htmlFor="phone" className="wb-label">
                  Phone number
                </label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+919072278060"
                />
              </div>
              <Button type="submit" disabled={loading} block>
                {loading ? 'Sending…' : 'Send verification code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="flex w-full flex-col gap-3">
              <div>
                <label htmlFor="otp" className="wb-label">
                  Verification code
                </label>
                <Input
                  ref={otpInputRef}
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="wb-mono text-center text-2xl tracking-[0.4em]"
                />
              </div>
              <Button type="submit" disabled={loading || otp.length < 6} block>
                {loading ? 'Verifying…' : 'Verify & sign in'}
              </Button>
              <Button type="button" variant="ghost" block onClick={handleBack}>
                Use a different number
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
